/**
 * Firestore transport selection for the pro admin frontend (KB-2606).
 *
 * Ported from marketing's `src/lib/firebase-firestore.ts` (KB-469, KB-648):
 * Safari (and iOS WebKit) reject the Firestore WebChannel transport mid-stream
 * once it has already worked once (AID=50 + access-control rejections), which
 * is a different failure shape than the cold-start case the SDK's own
 * `experimentalAutoDetectLongPolling` heuristic is tuned for — auto-detect
 * takes ~15-20s to decide WebChannel is broken here. Forcing long-polling
 * up front on Safari/WebKit skips that window entirely; Chrome/Firefox/others
 * keep the more-efficient auto-detect path.
 *
 * `initializeFirestore(app, settings)` MUST run before any `getFirestore(app)`
 * call on the same `FirebaseApp` — otherwise the SDK has already minted a
 * default-settings Firestore and `initializeFirestore` throws
 * `failed-precondition`. `initFirestoreWithTransport` treats that one error
 * code as a race-loser fallback (accept whatever was already minted) and lets
 * every other error propagate, so a real misconfiguration fails loudly
 * instead of silently shipping a Firestore without the transport override.
 *
 * `fsMod` is injected (rather than imported directly) so tests can supply a
 * fake `initializeFirestore` / `getFirestore` pair without mocking the
 * `firebase/firestore` module.
 */
import type { FirebaseApp } from "firebase/app";
import type { Firestore } from "firebase/firestore";

type FsInitMod = {
  initializeFirestore: (app: FirebaseApp, settings: FirestoreTransportSettings) => Firestore;
  getFirestore: (app: FirebaseApp) => Firestore;
};

type FirestoreTransportSettings =
  | { experimentalForceLongPolling: true }
  | { experimentalAutoDetectLongPolling: true };

/**
 * KB-648 — Safari / WebKit detection for Firestore transport selection.
 * The UA contains the literal "Safari" on desktop Safari + every iOS WebKit
 * browser (iPad / iPhone / iPod — Apple's WebView policy forces every iOS
 * browser onto WebKit), and is absent from non-WebKit browsers. Chromium-based
 * browsers (Chrome, Edge, Opera, Brave, Arc, Chromium itself) ALSO carry
 * "Safari" in their UA for legacy compatibility, so the discriminator is:
 * contains "Safari" AND NOT one of the Chromium markers.
 */
export function isSafariWebKit(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
  if (!ua) return false;
  return /Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR/i.test(ua);
}

export function pickTransportSettings(): FirestoreTransportSettings {
  return isSafariWebKit()
    ? { experimentalForceLongPolling: true }
    : { experimentalAutoDetectLongPolling: true };
}

export function initFirestoreWithTransport(app: FirebaseApp, fsMod: FsInitMod): Firestore {
  const transportSettings = pickTransportSettings();
  try {
    return fsMod.initializeFirestore(app, transportSettings);
  } catch (err) {
    if ((err as { code?: string } | null)?.code !== "failed-precondition") {
      throw err;
    }
    return fsMod.getFirestore(app);
  }
}
